import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useWebSocket } from './WebSocketContext';

import './create-packit.css';

// Import your working form components
import CreatePackitForm from './CreatePackitForm';
import QuickTripForm from './QuickTripForm';
import TripMoodForm from './TripMoodForm';

const getUserFromToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub;
  } catch (e) {
    return null;
  }
};

const CreatePackitFlow = () => {
  const location = useLocation();
  const prefillRef = useRef(location.state?.prefill || null);
  const fromRouteRef = useRef(location.state?.from || null);
  const [flowStep, setFlowStep] = useState(prefillRef.current ? 'detailed' : 'selection'); // 'selection', 'quick', 'detailed', 'feeling', 'random'
  const [formData, setFormData] = useState({
    tripName: '',
    description: '',
    startDate: null,
    endDate: null,
    isFlexibleDate: false,
    category: '',
    tripType: '',
    includesKids: false,
    includesPets: false,
    receiveAlerts: false,
    alertFrequency: '',
    packingNeeds: [], 
    interests: [],
    selectedColors: [],
    checkpoints: [],
    destination: '',
    goals: [],
    desires: [],
    feelings: [],
    primaryTravelMode: '',
    secondaryTravelModes: []
  });

  // Destinations state for quick trip and trip mood forms
  const [destinations, setDestinations] = useState([
    { id: Date.now(), name: '', minimumNumberOfDesiredDaysAtLocation: 1, isStart: false, isEnd: false }
  ]);
  
  const navigate = useNavigate();
  const { connectionId } = useWebSocket();
  const apiUrl = process.env.REACT_APP_API_URL;

  // User plan for text flow validation
  const [userPlan, setUserPlan] = useState('');
  const [packitTrialComplete, setPackitTrialComplete] = useState(false);
  const [freeTextInput, setFreeTextInput] = useState('');
  const [isSubmittingFreeText, setIsSubmittingFreeText] = useState(false);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = (await fetchAuthSession()).tokens?.idToken?.toString();
        const user = getUserFromToken(token);
        if (!user || !apiUrl) return;
        const headers = {
          'Content-type': 'application/json; charset=UTF-8',
          'Authorization': `Bearer ${token}`,
        };
        const response = await axios.get(`${apiUrl}/users/${encodeURIComponent(user)}`, { headers });
        const userData = response.data?.[0];
        if (userData) {
          setUserPlan(userData.plan || '');
          setPackitTrialComplete(Boolean(userData.packitTrialComplete));
        }
      } catch (err) {
        console.error('Error fetching user info:', err);
      }
    };
    fetchUserInfo();
  }, [apiUrl]);

  const handlePrefillBack = () => {
    if (fromRouteRef.current) {
      navigate(fromRouteRef.current);
    } else {
      navigate('/app');
    }
  };

  // Predefined options for different flows
  const goalOptions = [
    'Relaxation', 'Adventure', 'Learning', 'Connection', 'Escape', 'Celebration'
  ];
  
  const desireOptions = [
    'Beach', 'Mountains', 'City', 'Countryside', 'Culture', 'Food', 'History', 'Nature'
  ];
  
  const feelingOptions = [
    'Excited', 'Peaceful', 'Energetic', 'Curious', 'Nostalgic', 'Inspired', 'Adventurous', 'Cozy'
  ];

  // Handle random trip generation
  const handleRandomTrip = async () => {
    const token = (await fetchAuthSession()).tokens?.idToken?.toString();

    if (!token) {
      alert('You need to be logged in to create a Pack-It.');
      return;
    }

    // Random trips are always 7 days, so show the standard message
    alert('Your Pack-It is being created! This will take a few moments. You\'ll be notified when your Pack-It is ready.');

    setIsSubmitting(true);

    const headers = {
      'Content-type': 'application/json; charset=UTF-8',
      'Authorization': `Bearer ${token}`,
    };

    // Generate random trip data
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    const endDate = new Date(nextWeek);
    endDate.setDate(endDate.getDate() + 7);

    const randomTripNames = [
      'Weekend Getaway', 'Adventure Week', 'Relaxation Retreat', 'City Explorer', 
      'Nature Escape', 'Cultural Journey', 'Beach Bliss', 'Mountain Adventure'
    ];

    // Random destinations for roll the dice
    const randomDestinations = [
      'Paris, France', 'Tokyo, Japan', 'New York, USA', 'London, UK', 
      'Rome, Italy', 'Barcelona, Spain', 'Amsterdam, Netherlands', 'Prague, Czech Republic',
      'Bangkok, Thailand', 'Sydney, Australia', 'Dubai, UAE', 'Reykjavik, Iceland'
    ];

    const randomDestination = randomDestinations[Math.floor(Math.random() * randomDestinations.length)];

    const payload = {
      name: randomTripNames[Math.floor(Math.random() * randomTripNames.length)],
      description: 'A randomly generated trip for you to enjoy!',
      startDate: nextWeek.toISOString(),
      endDate: endDate.toISOString(),
      tripType: 'solo',
      isFlexibleDate: true,
      includesKids: false,
      includesPets: false,
      alertFrequency: 'daily',
      packingNeeds: ['lightweight'],
      interests: ['attractions'],
      checkpoints: [{
        checkpointType: 'destination',
        name: randomDestination,
        minimumNumberOfDesiredDaysAtLocation: 7,
        isStart: true,
        isEnd: true
      }],
      destination: randomDestination,
      colorCategory: null,
      ClientID: connectionId
    };

    try {
      const response = await axios.post(`${apiUrl}/briefings`, payload, { headers });
  
      if (response.status === 201 || response.status === 200) {
        navigate('/pack-it');
      } else {
        throw new Error('Failed to create Pack-It');
      }
    } catch (error) {
      console.error('Error creating Pack-It:', error);
      alert('An error occurred while creating your Pack-It. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Selection Screen Component
  const SelectionScreen = () => (
    <div className="create-packit-container">
      <h1>Create Your Pack-It</h1>
      <div className="flow-selection">
        <div className="flow-options">
          <button 
            className="flow-option"
            onClick={() => {
              setDestinations([{ id: Date.now(), name: '', minimumNumberOfDesiredDaysAtLocation: 1, isStart: false, isEnd: false }]);
              setFormData(prev => ({ ...prev, primaryTravelMode: '', secondaryTravelModes: [] }));
              setFlowStep('quick');
            }}
          >
            <div className="flow-icon">⚡</div>
            <h3>Quick Trip</h3>
            <p>Basic information and destination only</p>
          </button>

          <button 
            className="flow-option"
            onClick={() => setFlowStep('detailed')}
          >
            <div className="flow-icon">📋</div>
            <h3>Custom Trip</h3>
            <p>Complete form with all options</p>
          </button>

          <button 
            className="flow-option"
            onClick={() => {
              setDestinations([{ id: Date.now(), name: '', minimumNumberOfDesiredDaysAtLocation: 1, isStart: false, isEnd: false }]);
              setFormData(prev => ({ ...prev, primaryTravelMode: '', secondaryTravelModes: [] }));
              setFlowStep('feeling');
            }}
          >
            <div className="flow-icon">💭</div>
            <h3>Trip Mood</h3>
            <p>Based on your goals, desires, and feelings</p>
          </button>

          <button 
            className="flow-option"
            onClick={() => handleRandomTrip()}
          >
            <div className="flow-icon">🎲</div>
            <h3>Roll the Dice</h3>
            <p>Generate a random trip for 1 week starting next week</p>
          </button>

          <button 
            className="flow-option flow-option-single"
            onClick={() => setFlowStep('text')}
          >
            <div className="flow-icon">✏️</div>
            <h3>Write your own</h3>
            <p>Type what you want in your own words.</p>
          </button>
        </div>

        <div className="button-group">
          <button
            type="button"
            className="create-note-button cancel"
            onClick={() => navigate('/pack-it')}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  // Quick Trip Form Component - now imported from separate file

  // Trip Mood Form Component - now imported from separate file

  // Simple input handler
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Helper functions for bubble toggles
  const handleGoalToggle = (goal) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.includes(goal)
        ? prev.goals.filter(g => g !== goal)
        : [...prev.goals, goal]
    }));
  };

  const handleDesireToggle = (desire) => {
    setFormData(prev => ({
      ...prev,
      desires: prev.desires.includes(desire)
        ? prev.desires.filter(d => d !== desire)
        : [...prev.desires, desire]
    }));
  };

  const handleFeelingToggle = (feeling) => {
    setFormData(prev => ({
      ...prev,
      feelings: prev.feelings.includes(feeling)
        ? prev.feelings.filter(f => f !== feeling)
        : [...prev.feelings, feeling]
    }));
  };

  // Travel mode handlers
  const handlePrimaryTravelModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      primaryTravelMode: mode
    }));
  };

  const handleSecondaryTravelModeToggle = (mode) => {
    setFormData(prev => ({
      ...prev,
      secondaryTravelModes: prev.secondaryTravelModes.includes(mode)
        ? prev.secondaryTravelModes.filter(m => m !== mode)
        : [...prev.secondaryTravelModes, mode]
    }));
  };

  // Destination handlers for quick trip and trip mood forms
  const handleAddDestination = () => {
    setDestinations([
      ...destinations,
      {
        id: Date.now(),
        name: '',
        minimumNumberOfDesiredDaysAtLocation: 1,
        isStart: false,
        isEnd: false
      }
    ]);
  };

  const handleRemoveDestination = (id) => {
    if (destinations.length > 1) {
      setDestinations(destinations.filter((destination) => destination.id !== id));
    }
  };

  const handleUpdateDestination = (id, key, value) => {
    setDestinations((currentDestinations) =>
      currentDestinations.map((destination) => {
        if (destination.id !== id) return destination;

        const updated = { ...destination, [key]: value };

        // If user selects 'isStart' or 'isEnd' = true -> set days to 0
        if ((key === 'isStart' || key === 'isEnd') && value === true) {
          updated.minimumNumberOfDesiredDaysAtLocation = 0;
        }

        return updated;
      })
    );
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFreeTextSubmit = async (e) => {
    e.preventDefault();
    if (!freeTextInput.trim()) {
      alert('Please enter some text to create your Pack-It.');
      return;
    }
    if (userPlan === 'Basic' && packitTrialComplete) {
      alert('You have reached your Pack-It trial limit. Please upgrade to Globe Trotter to create more Pack-Its.');
      navigate('/pack-it');
      return;
    }
    if (userPlan === 'Adventurer') {
      alert('Pack-Its are only available for Globe Trotter plan users. Please upgrade to Globe Trotter to create Pack-Its.');
      navigate('/pack-it');
      return;
    }
    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    if (!token) {
      alert('You need to be logged in to create a Pack-It.');
      return;
    }
    setIsSubmittingFreeText(true);
    const headers = {
      'Content-type': 'application/json; charset=UTF-8',
      'Authorization': `Bearer ${token}`,
    };
    const payload = { text: freeTextInput.trim(), metadata: {} };
    try {
      const response = await axios.post(`${apiUrl}/briefings/from-text`, payload, { headers });
      if (response.status === 201 || response.status === 200) {
        alert('Your Pack-It is being created! This will take a few moments. You\'ll be notified when your Pack-It is ready.');
        setFreeTextInput('');
        navigate('/pack-it');
      } else {
        throw new Error('Failed to create Pack-It');
      }
    } catch (error) {
      console.error('Error creating Pack-It from text:', error);
      alert('An error occurred while creating your Pack-It. Please try again.');
    } finally {
      setIsSubmittingFreeText(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const token = (await fetchAuthSession()).tokens?.idToken?.toString();

    if (!token) {
      alert('You need to be logged in to create a Pack-It.');
      return;
    }

    // Calculate trip duration in days
    let tripDuration = 0;
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      tripDuration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
    }

    // Show appropriate message based on trip duration
    if (tripDuration > 7) {
      alert('Your Pack-It is being created! Since your trip is over 7 days, our system will implement deep research to provide you with comprehensive recommendations. This process will likely take 10 minutes or longer. You\'ll be notified when your Pack-It is ready.');
    } else {
      alert('Your Pack-It is being created! This will take a few moments. You\'ll be notified when your Pack-It is ready.');
    }

    setIsSubmitting(true);

    const headers = {
      'Content-type': 'application/json; charset=UTF-8',
      'Authorization': `Bearer ${token}`,
    };
  
    const payload = {
      name: formData.tripName,
      description: formData.description,
      startDate: formData.startDate ? formData.startDate.toISOString() : null,
      endDate: formData.endDate ? formData.endDate.toISOString() : null,
      tripType: formData.tripType,
      isFlexibleDate: formData.isFlexibleDate,
      includesKids: formData.includesKids,
      includesPets: formData.includesPets,
      alertFrequency: formData.alertFrequency,
      packingNeeds: formData.packingNeeds,
      interests: formData.interests,
      checkpoints: [],
      colorCategory: formData.selectedColors[0] || null,
      primaryTravelMode: formData.primaryTravelMode || null,
      secondaryTravelModes: formData.secondaryTravelModes || [],
      ClientID: connectionId
    };

    // Add flow-specific data and create checkpoints from destinations
    if (flowStep === 'quick') {
      // Create checkpoints from destinations array for quick trip
      if (destinations.length > 0 && destinations.some(d => d.name.trim())) {
        payload.checkpoints = destinations
          .filter(d => d.name.trim()) // Only include destinations with names
          .map((destination) => ({
            checkpointType: 'destination',
            name: destination.name,
            minimumNumberOfDesiredDaysAtLocation: destination.minimumNumberOfDesiredDaysAtLocation || 1,
            isStart: destination.isStart || false,
            isEnd: destination.isEnd || false
          }));
        // Keep destination field for backward compatibility (use first destination)
        payload.destination = destinations[0]?.name || '';
      }
    } else if (flowStep === 'feeling') {
      payload.goals = formData.goals;
      payload.desires = formData.desires;
      payload.feelings = formData.feelings;
      // Create checkpoints from destinations array for trip mood
      if (destinations.length > 0 && destinations.some(d => d.name.trim())) {
        payload.checkpoints = destinations
          .filter(d => d.name.trim()) // Only include destinations with names
          .map((destination) => ({
            checkpointType: 'destination',
            name: destination.name,
            minimumNumberOfDesiredDaysAtLocation: destination.minimumNumberOfDesiredDaysAtLocation || 1,
            isStart: destination.isStart || false,
            isEnd: destination.isEnd || false
          }));
        // Keep destination field for backward compatibility (use first destination)
        payload.destination = destinations[0]?.name || '';
      }
    } else if (flowStep === 'random') {
      // For roll the dice, we'll need to handle this in the random trip generation
      // The destination will be set by the random trip API response
    }
  
    try {
      const response = await axios.post(`${apiUrl}/briefings`, payload, { headers });
  
      if (response.status === 201 || response.status === 200) {
        navigate('/pack-it');
      } else {
        throw new Error('Failed to create Pack-It');
      }
    } catch (error) {
      console.error('Error creating Pack-It:', error);
      alert('An error occurred while creating your Pack-It. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Render based on flow step
  switch (flowStep) {
    case 'selection':
      return <SelectionScreen />;
    case 'quick':
      return <QuickTripForm 
        formData={formData}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        onBack={() => {
          setDestinations([{ id: Date.now(), name: '', minimumNumberOfDesiredDaysAtLocation: 1, isStart: false, isEnd: false }]);
          setFormData(prev => ({ ...prev, primaryTravelMode: '', secondaryTravelModes: [] }));
          setFlowStep('selection');
        }}
        isSubmitting={isSubmitting}
        destinations={destinations}
        handleAddDestination={handleAddDestination}
        handleRemoveDestination={handleRemoveDestination}
        handleUpdateDestination={handleUpdateDestination}
        handlePrimaryTravelModeChange={handlePrimaryTravelModeChange}
        handleSecondaryTravelModeToggle={handleSecondaryTravelModeToggle}
      />;
    case 'detailed':
      return (
        <CreatePackitForm
          onBack={prefillRef.current ? handlePrefillBack : () => setFlowStep('selection')}
          initialFormData={prefillRef.current?.formData}
          initialDestinations={prefillRef.current?.destinations}
          selectedNotesSummary={prefillRef.current?.selectedNotes}
        />
      );
    case 'text':
      return (
        <div className="create-packit-container">
          <h1>Create Your Pack-It</h1>
          <div className="create-packit-text-container">
            <form className="create-packit-text-form" onSubmit={handleFreeTextSubmit}>
              <div className="form-section">
                <h3 className="section-title">Create Pack-It with Text</h3>
                <p className="section-subtitle">Simply describe your trip in your own words and let us create your Pack-It for you</p>
                <textarea
                  value={freeTextInput}
                  onChange={(e) => setFreeTextInput(e.target.value)}
                  placeholder="E.g., I want to visit Telluride, CO in the next month."
                  rows="5"
                  className="free-text-input"
                />
                <div className="button-group">
                  <button
                    type="button"
                    className="create-note-button cancel"
                    onClick={() => setFlowStep('selection')}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="create-note-button"
                    disabled={isSubmittingFreeText || !freeTextInput.trim()}
                  >
                    {isSubmittingFreeText ? 'Creating...' : 'Submit'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      );
    case 'feeling':
      return <TripMoodForm 
        formData={formData}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        onBack={() => {
          setDestinations([{ id: Date.now(), name: '', minimumNumberOfDesiredDaysAtLocation: 1, isStart: false, isEnd: false }]);
          setFormData(prev => ({ ...prev, primaryTravelMode: '', secondaryTravelModes: [] }));
          setFlowStep('selection');
        }}
        goalOptions={goalOptions}
        desireOptions={desireOptions}
        feelingOptions={feelingOptions}
        handleGoalToggle={handleGoalToggle}
        handleDesireToggle={handleDesireToggle}
        handleFeelingToggle={handleFeelingToggle}
        isSubmitting={isSubmitting}
        destinations={destinations}
        handleAddDestination={handleAddDestination}
        handleRemoveDestination={handleRemoveDestination}
        handleUpdateDestination={handleUpdateDestination}
        handlePrimaryTravelModeChange={handlePrimaryTravelModeChange}
        handleSecondaryTravelModeToggle={handleSecondaryTravelModeToggle}
      />;
    default:
      return <SelectionScreen />;
  }
};

export default CreatePackitFlow;
